import { Page } from "../enums/Page";
import { css, htmlArray } from "../helpers/webComponents";
import { PlayerService } from "../services/PlayerService";
/** CSS affecting the {@link RegisterComponent} */
const loginStyles: string = css`
:host {
    font-family: "Onesize";
    width: 100%;
    max-width: 1024px;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column; 
}

/** Styles for the title section */
.title {
    text-align: center;
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 20px;
    color: white;
}

/** Styles for the content container */
.content {
    width: 90%;
    max-width: 600px;
    padding: 30px;
    background-color: #666666;
    opacity: 0.95;
    border: 10px solid #4e4e4e;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

/** Styles for the form inside the content container */
.content form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/** Styles for form labels */
.content label {
    margin: 10px 0;
    font-size: 18px;
    color: white;
}

/** Styles for form input fields */
.content input {
    padding: 12px;
    width: 100%;
    font-size: 18px;
    border: 2px solid #4e4e4e;
    background-color: #3c3c3c;
    color: white;
    text-align: center;
    border-radius: 5px;
}

/** Success message styling */
.message.success {
    color: #32e113;
}

/** Error message styling */
.message.error {
    color: red;
}

/** Styles for buttons in the form */
.return-button,
.register-button {
    font-family: "Onesize", sans-serif;
    font-size: 20px; 
    background-color: #780606;
    border: 1px solid #6d6d6d;
    padding: 15px 20px;
    margin-top: 20px;
    text-transform: uppercase;
    cursor: pointer;
    width: 100%;
    text-align: center;
    color: white;
    border-radius: 5px;
    transition: background-color 0.01s ease-in-out;
}

/** Hover effect for buttons */
.return-button:hover,
.register-button:hover {
    background-color: #bebbbb;
    color: black;
}
`;
type data = {
    message: string;
    playersessionID?: string;
    playersessionJSON?: string;
};

export class LoginComponent extends HTMLElement {
    /**
     * Lifecycle method triggered when the component is attached to the DOM.
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        this.render();
    }

    /**
     * Renders the component content inside the shadow DOM.
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${loginStyles}
            </style>

            ${this.renderTitle()}
            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        this.addFormListener();

        const returnButton: Element | null = this.shadowRoot.querySelector(".return-button");
        returnButton?.addEventListener("click", () => this.returnToRoom());
    }

    /**
     * Renders the title element.
     *
     * @returns {string} HTML string for the title.
     */
    private renderTitle(): string {
        return "<div class=\"title\">Login</div>";
    }

    /**
     * Renders the content section, including the login form.
     *
     * @returns {string} HTML string for the content section.
     */
    private renderContent(): string {
        return `
            <div class="content">
                <form id="login-form">
                    <label for="username">Username:</label>
                    <input type="text" id="username" name="username" required>

                    <label for="password">Password:</label>
                    <input type="password" id="password" name="password" required>

                    <button type="submit" class="register-button">Log in</button>
                    <button class="return-button">Return to the game</button>
                </form>
                <div id="message" class="message"></div>
            </div>
        `;
    }

    /**
     * Adds an event listener to the login form to handle submission.
     */
    private addFormListener(): void {
        if (!this.shadowRoot) return;

        const form: HTMLFormElement | null = this.shadowRoot.querySelector("#login-form");
        const messageDiv: HTMLElement | null = this.shadowRoot.querySelector("#message");

        if (!form || !messageDiv) return;

        form.addEventListener("submit", async event => {
            event.preventDefault();

            type LoginRequestBody = {
                username: string;
                password: string;
            };

            const formData: FormData = new FormData(form);
            const requestBody: LoginRequestBody = {
                username: formData.get("username") as string,
                password: formData.get("password") as string,
            };

            try {
                const response: Response = await fetch(`${VITE_API_URL}milad/login`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(requestBody),
                });

                if (response.ok) {
                    localStorage.setItem("userSession", "Active");
                }

                if (!response.ok) {
                    throw new Error("Login error");
                }

                const data: data = await response.json() as data;

                const playerService: PlayerService = new PlayerService();

                if (data.playersessionID && data.playersessionJSON) {
                    playerService.setPlayerSessionId(data.playersessionID);

                    sessionStorage.setItem("playersessionID", data.playersessionID);
                    sessionStorage.setItem("playersessionJSON", data.playersessionJSON);
                }

                messageDiv.textContent = data.message || "Login successful!";
                messageDiv.className = "message success";
            }
            catch (error) {
                messageDiv.textContent = "Login error: " + "User does not exist";
                messageDiv.className = "message error";
                console.log(error);
            }
        });
    }

    /**
     * Handles returning to the game by dispatching a custom event.
     */
    private returnToRoom(): void {
        const event: CustomEvent = new CustomEvent("game-switch-page", {
            detail: { page: Page.Canvas },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }
}
